
import React from 'react';
import {
  UserIcon,
  MicrophoneIcon,
  MagicWandIcon,
  PlayIcon,
  FloppyDiskIcon,
  CircleNotchIcon,
  CaretDownIcon,
  CaretUpIcon,
  TrashIcon,
  SpeakerHighIcon,
  PencilSimpleIcon,
  XIcon,
  PlusIcon,
  PauseIcon,
} from '@phosphor-icons/react';
import { CharacterVoice, VoiceMode, SavedVoiceSample } from '../../types';
import { getVoiceDisplayName, getVoiceModeLabel } from '../../lib/characterVoiceService';
import { CharacterArchetype } from '../../lib/physiognomyVoicePrompt';
import LanguageSelector, { LanguageBadge } from './LanguageSelector';
import PresetSpeakerPicker from './PresetSpeakerPicker';
import VoiceCloneUploader from './VoiceCloneUploader';
import VoiceDesignInput from './VoiceDesignInput';
import AudioPlayer from './AudioPlayer';
import { useVoiceConfigurator, TABS, UseVoiceConfiguratorProps } from './useVoiceConfigurator';
import { CreditCostBadge } from '../CreditCostBadge';
import { SPECIAL_CREDIT_COSTS } from '../../lib/credits';

// Export type derived from the hook
export type VoiceConfiguratorState = ReturnType<typeof useVoiceConfigurator>;

// -----------------------------------------------------------------------------
// Sub-component: Voice Header (Quote, Directions, Testing)
// -----------------------------------------------------------------------------
interface VoiceHeaderProps {
  state: VoiceConfiguratorState;
  className?: string;
}

export const VoiceHeaderSection: React.FC<VoiceHeaderProps> = ({ state, className = '' }) => {
  const {
    existingVoice,
    setIsExpanded,
    testAudioBlob,
    testAudioUrl,
    isPlayerExiting,
    language,
    setLanguage,
    testLine,
    setTestLine,
    testLinePlaceholder,
    styleInstruction,
    setStyleInstruction,
    directionPlaceholder,
    handleGenerateTest,
    isValid,
    isGeneratingTest,
    handleSave,
    isSaving,
    selectedSavedSample,
  } = state;

  // When viewing a saved sample, hide editing controls
  const isViewingSavedSample = !!selectedSavedSample;

  return (
    <div className={`flex flex-col relative transition-all duration-300 ${className}`}>
      {/* Audio Preview - appears with height animation */}
      <div className={`overflow-hidden transition-all duration-300 ease-out ${(testAudioBlob || testAudioUrl) && !isPlayerExiting ? 'max-h-[100px] opacity-100 mb-6' : 'max-h-0 opacity-0 mb-0'}`}>
        {testAudioBlob ? (
          <AudioPlayer blob={testAudioBlob} showWaveform autoPlay />
        ) : testAudioUrl ? (
          <AudioPlayer src={testAudioUrl} showWaveform />
        ) : null}
      </div>

      {/* Quote section */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="block text-sm font-medium text-dust">
            Quote
          </label>
          {!isViewingSavedSample && (
            <LanguageSelector value={language} onChange={setLanguage} compact />
          )}
        </div>
        <div className="relative group">
          <textarea
            value={testLine}
            onChange={(e) => !isViewingSavedSample && setTestLine(e.target.value)}
            placeholder={testLinePlaceholder}
            rows={3}
            readOnly={isViewingSavedSample}
            className={`w-full px-4 py-3 bg-white/5 border border-transparent rounded-xl text-sm text-white placeholder:text-white/20 resize-none transition-all ${
              isViewingSavedSample
                ? 'cursor-default'
                : 'focus:outline-none focus:bg-white/10 focus:ring-1 focus:ring-slate-blue/50'
            }`}
          />
        </div>
        {!isViewingSavedSample && (
          <div className="flex items-center justify-between mt-2 px-1">
            <p className="text-xs text-dust/40">
              What is something this character would say?
            </p>
            <span className={`text-xs ${testLine.length > 0 && testLine.length < 450 ? 'text-amber-400/70' : 'text-dust/30'}`}>
              {testLine.length > 0 ? `${testLine.length}/450 min` : '450 char min (~30 sec)'}
            </span>
          </div>
        )}
      </div>

      {/* Vocal Direction - below quote */}
      <div className="mt-6">
        <label className="block text-sm font-medium text-dust mb-3">
          Vocal Direction
          {!isViewingSavedSample && <span className="text-dust/30 ml-2 text-xs font-normal">(Optional)</span>}
        </label>
        <textarea
          value={styleInstruction}
          onChange={(e) => !isViewingSavedSample && setStyleInstruction(e.target.value)}
          placeholder={isViewingSavedSample ? '' : directionPlaceholder}
          readOnly={isViewingSavedSample}
          className={`w-full h-24 bg-white/5 border border-transparent rounded-xl p-4 text-sm text-white placeholder:text-white/20 resize-none transition-all ${
            isViewingSavedSample
              ? 'cursor-default'
              : 'focus:outline-none focus:bg-white/10 focus:ring-1 focus:ring-slate-blue/50'
          }`}
        />
        {!isViewingSavedSample && (
          <p className="mt-2 text-xs text-dust/40 px-1">
            Like a director guiding an actor—shape the emotional delivery and pacing for each performance.
          </p>
        )}
      </div>

      {/* Test and Save buttons - hidden when viewing a saved sample */}
      {!isViewingSavedSample && (
        <div className="flex items-center justify-start gap-3 mt-6 pt-6 border-t border-white/5">
          <button
            onClick={handleGenerateTest}
            disabled={!isValid() || isGeneratingTest || (testLine.length > 0 && testLine.length < 450)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 text-white hover:bg-white/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isGeneratingTest ? (
              <>
                <CircleNotchIcon className="w-4 h-4 animate-spin" />
                <span className="text-sm">Testing...</span>
              </>
            ) : (
              <>
                <PlayIcon className="w-4 h-4" />
                <span className="text-sm">Test</span>
                <CreditCostBadge cost={SPECIAL_CREDIT_COSTS.qwenTTS} />
              </>
            )}
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || (!testAudioBlob && !testAudioUrl)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-blue text-white hover:bg-[#8583f9] transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-slate-blue/20"
            title={!testAudioBlob && !testAudioUrl ? 'Test the voice first to enable saving' : undefined}
          >
            {isSaving ? (
              <>
                <CircleNotchIcon className="w-4 h-4 animate-spin" />
                <span className="text-sm">Saving...</span>
              </>
            ) : (
              <>
                <FloppyDiskIcon className="w-4 h-4" />
                <span className="text-sm">Save</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

// -----------------------------------------------------------------------------
// Sub-component: Voice Settings (Tabs and Forms)
// -----------------------------------------------------------------------------
interface VoiceBodyProps {
  state: VoiceConfiguratorState;
  headImageUrl?: string;
  bodyImageUrl?: string;
  referenceSheetUrl?: string;
  archetype?: CharacterArchetype;
  characterName?: string;
  characterDescription?: string;
  className?: string;
}

export const VoiceSettingsSection: React.FC<VoiceBodyProps> = ({
  state,
  headImageUrl,
  bodyImageUrl,
  referenceSheetUrl,
  archetype,
  characterName,
  characterDescription,
  className = ''
}) => {
  const {
    activeTab,
    setActiveTab,
    selectedSpeaker,
    setSelectedSpeaker,
    referenceAudioBase64,
    setReferenceAudioBase64,
    referenceTranscript,
    setReferenceTranscript,
    referenceAudioDuration,
    setReferenceAudioDuration,
    voiceDescription,
    setVoiceDescription,
    selectedSavedSample,
  } = state;

  // Hide entire section when viewing a saved sample
  if (selectedSavedSample) {
    return null;
  }

  return (
    <div className={`flex flex-col ${className}`}>
      {/* Tabs */}
      <div className="flex gap-2 pb-2 mb-4">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;

          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl text-sm font-medium transition-all ${isActive
                ? 'bg-white/10 text-white shadow-sm'
                : 'text-dust/60 hover:bg-white/5 hover:text-dust'
                }`}
            >
              <Icon className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div className="p-6 bg-zinc-900/50 border border-white/5 rounded-2xl">
        {activeTab === 'custom' && (
          <PresetSpeakerPicker
            value={selectedSpeaker}
            onChange={setSelectedSpeaker}
          />
        )}

        {activeTab === 'clone' && (
          <VoiceCloneUploader
            referenceAudio={referenceAudioBase64}
            referenceTranscript={referenceTranscript}
            referenceAudioDuration={referenceAudioDuration}
            onAudioChange={setReferenceAudioBase64}
            onTranscriptChange={setReferenceTranscript}
            onDurationChange={setReferenceAudioDuration}
          />
        )}

        {activeTab === 'design' && (
          <VoiceDesignInput
            value={voiceDescription}
            onChange={setVoiceDescription}
            headImageUrl={headImageUrl}
            bodyImageUrl={bodyImageUrl}
            referenceSheetUrl={referenceSheetUrl}
            archetype={archetype}
            characterName={characterName}
            characterDescription={characterDescription}
          />
        )}
      </div>
    </div>
  );
};


interface InlineVoiceConfiguratorProps extends UseVoiceConfiguratorProps {
  compact?: boolean;
  splitLayout?: boolean; // Legacy prop, can ignore or use for internal logic
}

export const InlineVoiceConfigurator: React.FC<InlineVoiceConfiguratorProps> = (props) => {
  const state = useVoiceConfigurator(props);
  const { isExpanded, setIsExpanded, existingVoice, language, setLanguage, activeTab, setActiveTab, selectedSpeaker, setSelectedSpeaker, referenceAudioBase64, setReferenceAudioBase64, referenceTranscript, setReferenceTranscript, referenceAudioDuration, setReferenceAudioDuration, voiceDescription, setVoiceDescription, testLine, setTestLine, testAudioBlob, isGeneratingTest, handleGenerateTest, handleSave, isSaving, isValid } = state;
  const { compact, onVoiceDeleted } = props;

  const getModeIcon = (mode: VoiceMode) => {
    switch (mode) {
      case 'custom_voice': return UserIcon;
      case 'voice_clone': return MicrophoneIcon;
      case 'voice_design': return MagicWandIcon;
      default: return SpeakerHighIcon;
    }
  };

  const getModeColor = (mode: VoiceMode) => {
    switch (mode) {
      case 'custom_voice': return 'bg-blue-500/20 text-blue-400';
      case 'voice_clone': return 'bg-emerald-500/20 text-emerald-400';
      case 'voice_design': return 'bg-violet-500/20 text-violet-400';
      default: return 'bg-slate-blue/20 text-slate-blue-soft';
    }
  };

  // ---------------------------------------------------------------------------
  // Default Inline Layout (Legacy)
  // ---------------------------------------------------------------------------

  if (!isExpanded && existingVoice) {
    const ModeIcon = getModeIcon(existingVoice.voice_mode);

    if (compact) {
      // Compact collapsed view implementation (similar to original code)
      // ... (truncated for brevity, assuming standard usage in SavedVoicesPanel)
      return (
        <div className="h-full rounded-2xl bg-carbon border border-graphite overflow-hidden p-4">
          {/* Simplified for now, just showing header */}
          <div className="flex items-center gap-2 mb-2">
            <SpeakerHighIcon className="w-5 h-5 text-slate-blue" />
            <span className="text-sm font-semibold text-dust">Voice</span>
          </div>
          <div className="flex items-center gap-2 mb-2">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center ${getModeColor(existingVoice.voice_mode)}`}>
              <ModeIcon className="w-3 h-3" />
            </div>
            <span className="text-sm text-white truncate">{getVoiceDisplayName(existingVoice)}</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setIsExpanded(true)} className="text-xs text-slate-blue">Edit</button>
            <LanguageBadge language={existingVoice.language} />
          </div>
        </div>
      );
    }

    // Full collapsed view
    return (
      <div className="rounded-2xl bg-carbon border border-graphite overflow-hidden">
        {existingVoice.preview_audio_url && <audio src={existingVoice.preview_audio_url} />}
        <div className="p-5">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getModeColor(existingVoice.voice_mode)}`}>
                <ModeIcon className="w-4 h-4" />
              </div>
              <div>
                <h3 className="font-medium text-white">{getVoiceDisplayName(existingVoice)}</h3>
                <span className={`inline-flex items-center gap-1 text-xs ${getModeColor(existingVoice.voice_mode)} px-2 py-0.5 rounded mt-1`}>
                  <ModeIcon className="w-3 h-3" />
                  <span>{getVoiceModeLabel(existingVoice.voice_mode)}</span>
                </span>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {onVoiceDeleted && (
                <button onClick={onVoiceDeleted} className="p-2 rounded-lg hover:bg-red-500/10 transition-colors group">
                  <TrashIcon className="w-4 h-4 text-iron group-hover:text-red-400" />
                </button>
              )}
            </div>
          </div>
          <p className="text-sm text-dust/50 mb-4 line-clamp-2">
            {existingVoice.voice_mode === 'custom_voice' ? `Preset: ${existingVoice.preset_speaker}` : existingVoice.voice_mode === 'voice_clone' ? 'Cloned from audio' : existingVoice.voice_description}
          </p>
          <div className="flex items-center justify-between">
            <LanguageBadge language={existingVoice.language} />
            <button onClick={() => setIsExpanded(true)} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-graphite text-dust hover:bg-iron transition-colors">
              <CaretDownIcon className="w-3.5 h-3.5" />
              <span className="text-xs">Edit</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Expanded View
  return (
    <div className="rounded-2xl bg-carbon border border-graphite overflow-hidden">
      {/* Re-use our new sub-components for the inline view too! */}
      <div className="p-6">
        <VoiceHeaderSection state={state} />
        <div className="h-px bg-graphite my-6" /> {/* Separator for inline view */}
        <VoiceSettingsSection
          state={state}
          headImageUrl={props.headImageUrl}
          bodyImageUrl={props.bodyImageUrl}
          referenceSheetUrl={props.referenceSheetUrl}
          archetype={props.archetype}
          characterName={props.characterName}
          characterDescription={props.characterDescription}
        />
      </div>
    </div>
  );
};

export default InlineVoiceConfigurator;
